import React from 'react';
import { motion } from 'framer-motion';
import { pageWrapperStyle } from '../../styles/pageStyles.js';
import './PolicyPage.css';

const WebsitePackagesTerms = () => {
  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.6 }}
      style={{ ...pageWrapperStyle, backgroundColor: 'var(--alt-background)' }}
    >
      <section className="policy-page section">
        <div className="container">
          <div className="policy-content">
            <h1>Website Packages Terms &amp; Conditions</h1>
            <p className="last-updated">Last updated: June 11, 2025</p>

            <p>Digital Fig Ltd (Company No. 15889833) ("the Agency", "we", "us", "our") and the Customer or Purchaser ("the Client", "you", "your") are the "Parties". This Agreement sets out the terms for our website packages.</p>

            <h2>1. Scope of Services</h2>
            <p>Depending on the chosen package, Digital Fig Ltd may provide:</p>
            <ul>
              <li>Website design and build</li>
              <li>Hosting, SSL, and technical maintenance</li>
              <li>CMS setup and access</li>
              <li>Course systems, booking systems, or e-commerce</li>
              <li>Branding or graphic design</li>
              <li>Optional add-ons or premium upgrades</li>
            </ul>
            <p>Any services outside the chosen package will require a separate quotation.</p>

            <h2>2. Package Inclusions</h2>
            <p>Your selected package may include:</p>
            <ul>
              <li>Hosting and SSL certificate</li>
              <li>CMS access for content, images, and page updates</li>
              <li>Security updates and technical maintenance</li>
              <li>Two design consultations</li>
              <li>SEO-ready configuration</li>
              <li>Basic setup of pages included in the selected plan</li>
            </ul>
            <p>A setup fee may apply unless stated otherwise. If the setup fee is waived, it is waived only if the Client completes the minimum contract term (12 months). Early cancellation will result in the full setup fee becoming payable.</p>

            <h2>3. Exclusions</h2>
            <p>This Agreement does not include:</p>
            <ul>
              <li>Ongoing content writing</li>
              <li>Photography or videography</li>
              <li>Custom software development</li>
              <li>Large data imports</li>
              <li>Migration from legacy systems</li>
              <li>Legal drafting for policies</li>
              <li>External platform integrations unless agreed</li>
            </ul>
            <p>These services can be quoted separately.</p>

            <h2>4. Additional Work</h2>
            <p>Work outside the agreed scope is chargeable at GBP 40 per hour, including:</p>
            <ul>
              <li>Additional design after sign-off</li>
              <li>Large content uploads</li>
              <li>Undoing errors caused by the Client</li>
              <li>Email troubleshooting or device configuration</li>
              <li>Third-party integrations or advanced technical work</li>
            </ul>

            <h2>5. Email Accounts (Domain-Based Emails)</h2>
            <ol>
              <li>Basic setup is included for the agreed number of email accounts.</li>
              <li>The Client is responsible for all email usage, password security, and GDPR compliance.</li>
              <li>The Agency is not liable for email loss, downtime, spam filtering, or third-party outages.</li>
              <li>Additional email accounts or configuration may incur extra fees.</li>
              <li>If hosting or domain services end, associated email accounts may be permanently deleted.</li>
            </ol>

            <h2>6. Hosting &amp; Maintenance</h2>
            <ul>
              <li>Hosting and SSL are included where we provide hosting.</li>
              <li>Security updates and core technical maintenance are included.</li>
              <li>Reasonable usage applies to storage, bandwidth, and CPU load.</li>
              <li>Excessive usage may require an upgrade or additional fees.</li>
            </ul>
            <p>Clients hosting their own websites are responsible for their own hosting environment.</p>

            <h2>7. Payment Terms</h2>
            <ul>
              <li>Payment is due within 14 days of invoice unless otherwise stated.</li>
              <li>Monthly packages renew automatically unless cancelled in writing.</li>
              <li>Late payments may result in suspension of hosting or access.</li>
              <li>Setup fees and domain charges are non-refundable.</li>
              <li>Domains or hosting will not be released until invoices are fully settled.</li>
            </ul>
            <p>The Client commits to a 12-month contract; early termination charges apply (see Section 13).</p>

            <h2>8. Client Responsibilities</h2>
            <ul>
              <li>Providing accurate and lawful content.</li>
              <li>Responding to design drafts and requests in a timely manner.</li>
              <li>Ensuring compliance with UK law and GDPR for their data.</li>
              <li>Securing passwords and managing staff access.</li>
            </ul>
            <p>The Agency is not responsible for errors caused by incorrect or late information from the Client.</p>

            <h2>9. Design, Revisions &amp; Sign-Off</h2>
            <ul>
              <li>The Client receives two design consultations.</li>
              <li>Once the design is approved, the project is considered signed off.</li>
              <li>Changes after sign-off are billable at GBP 40 per hour.</li>
              <li>A "Redraft" is considered a significant design change.</li>
            </ul>

            <h2>10. Domains</h2>
            <ul>
              <li>Domains purchased through Digital Fig may include a free first-year registration (if stated).</li>
              <li>Domains renew at standard registrar rates and must be renewed on time by the Client.</li>
              <li>Domains cannot be transferred until all fees are paid.</li>
            </ul>
            <p>Digital Fig is not liable for loss caused by expired domains.</p>

            <h2>11. Liability &amp; Indemnity</h2>
            <ul>
              <li>The Agency is not liable for loss of profits, revenue, or business.</li>
              <li>We are not responsible for downtime or outages from third-party providers.</li>
              <li>We are not liable for client misuse, content errors, or GDPR breaches.</li>
              <li>Issues arising from Client-managed hosting fall under the Client's responsibility.</li>
              <li>The Client agrees to indemnify the Agency against claims relating to content, data, or misuse of the website.</li>
            </ul>
            <p>Total liability shall not exceed the value of fees paid during the Agreement.</p>

            <h2>12. Intellectual Property</h2>
            <ul>
              <li>The Client owns final approved content provided by them.</li>
              <li>The Agency owns all frameworks, themes, templates, and source code unless explicitly transferred.</li>
              <li>Plugins and third-party tools may require separate licences.</li>
              <li>Unpaid invoices void all licences and usage rights until settled.</li>
            </ul>

            <h2>13. Early Termination</h2>
            <ul>
              <li>Clients on a 12-month contract may cancel with written notice.</li>
              <li>The remaining contract balance becomes immediately payable.</li>
              <li>Any waived setup fee becomes payable in full.</li>
              <li>Domain and hosting fees remain payable.</li>
              <li>Upon full payment, a backup ZIP and credentials will be provided.</li>
            </ul>

            <h2>14. End of Contract</h2>
            <ul>
              <li>The Client may request a full backup ZIP of files and database.</li>
              <li>Migration to another provider may require technical assistance (chargeable).</li>
              <li>Hosting or email will cease if not renewed.</li>
            </ul>

            <h2>15. Attribution</h2>
            <p>The website may display a small footer credit ("Powered by Digital Fig"). This must not be removed without written consent.</p>

            <h2>16. Portfolio Use</h2>
            <p>Digital Fig may use completed work in its portfolio, showreels, or advertising. No confidential information will be shared.</p>

            <h2>17. Confidentiality</h2>
            <p>Both Parties agree to confidentiality regarding business and technical information, unless disclosure is required by law.</p>

            <h2>18. Force Majeure</h2>
            <p>Neither Party is liable for delays caused by events outside their control (including outages, strikes, or natural disasters).</p>

            <h2>19. Governing Law</h2>
            <p>These Terms are governed by the laws of England and Wales, and disputes fall under its courts.</p>

            <h2>20. Acceptance of Terms</h2>
            <p>By paying an invoice or instructing us to begin work (including via email, WhatsApp, or text message), you confirm that you have read and agreed to these Terms &amp; Conditions. No physical signature is required for this Agreement to become binding.</p>
          </div>
        </div>
      </section>
    </motion.div>
  );
};

export default WebsitePackagesTerms;
